import { Plus, X } from "lucide-react";
import { useContent } from "@/lib/content";

const stopNav = (e) => { e.preventDefault(); e.stopPropagation(); };

// Editable list stored as metadata.items under key `k`. Items are strings or flat objects.
export const EditableList = ({ k, items: defaults, newItem, addLabel = "Add item", className = "", children }) => {
  const { editMode, get, stage } = useContent();
  const items = get(k)?.metadata?.items ?? defaults;
  const commit = (next) => stage(k, { content: "", metadata: { items: next } });

  const makeField = (index) => ({ field, as: Tag = "span", className: cls = "", multiline = false }) => {
    const item = items[index];
    const value = field ? item?.[field] ?? "" : item ?? "";
    const pre = multiline ? " whitespace-pre-line" : "";
    if (!editMode) return <Tag className={cls + pre}>{value}</Tag>;
    return (
      <Tag
        className={`${cls}${pre} live-editable`}
        contentEditable
        suppressContentEditableWarning
        spellCheck={false}
        data-live-key={`${k}.${index}${field ? "." + field : ""}`}
        onClick={stopNav}
        onKeyDown={(e) => { if (e.key === "Enter" && !multiline) { e.preventDefault(); e.currentTarget.blur(); } }}
        onBlur={(e) => {
          const text = e.currentTarget.innerText.replace(/\u00a0/g, " ").trim();
          if (text === value) return;
          const next = items.map((it, i) => (i !== index ? it : field ? { ...it, [field]: text } : text));
          commit(next);
        }}
      >
        {value}
      </Tag>
    );
  };

  return (
    <>
      <div className={className} data-live-list={k}>
        {items.map((item, index) => (
          <div key={index} className={editMode ? "relative" : "contents"}>
            {children(item, index, makeField(index))}
            {editMode && (
              <button type="button" aria-label="Remove item" data-testid={`live-list-remove-${k}-${index}`}
                onClick={(e) => { stopNav(e); commit(items.filter((_, i) => i !== index)); }}
                className="absolute -top-2 -right-2 z-20 p-1 rounded-full bg-lipstick-red text-white shadow hover:scale-110 transition-transform">
                <X className="w-3 h-3" />
              </button>
            )}
          </div>
        ))}
      </div>
      {editMode && (
        <div className="text-center mt-6">
          <button type="button" data-testid={`live-list-add-${k}`} onClick={(e) => { stopNav(e); commit([...items, typeof newItem === "function" ? newItem() : newItem]); }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full border-2 border-dashed border-champagne-gold text-champagne-gold font-body text-xs tracking-widest uppercase hover:bg-champagne-gold/10 transition-colors">
            <Plus className="w-4 h-4" /> {addLabel}
          </button>
        </div>
      )}
    </>
  );
};
